"use client";

import { useState } from "react";
import CdnImage from "@/components/CdnImage";
import { FaStar } from "react-icons/fa";
import { useT } from "@/hooks/useT";

type Testimonial = { text: string; name: string; role: string; image: string };

export default function TestimonialsSection() {
  const t = useT();
  const ROW_A: Testimonial[] = [
    { text: t("testimonials.r1.t1"), name: "Alex Chen", role: t("testimonials.role.cryptoTrader"), image: "/frenpfp.png" },
    { text: t("testimonials.r1.t2"), name: "Sarah Williams", role: t("testimonials.role.defiInvestor"), image: "/frenpfp.png" },
    { text: t("testimonials.r1.t3"), name: "Marcus Johnson", role: t("testimonials.role.polymarketPro"), image: "/frenpfp.png" },
    { text: t("testimonials.r1.t4"), name: "Emma Davis", role: t("testimonials.role.predictionMarketsTrader"), image: "/frenpfp.png" },
  ];
  const ROW_B: Testimonial[] = [
    { text: t("testimonials.r2.t1"), name: "James Miller", role: t("testimonials.role.quantTrader"), image: "/frenpfp.png" },
    { text: t("testimonials.r2.t2"), name: "Olivia Brown", role: t("testimonials.role.riskManager"), image: "/frenpfp.png" },
    { text: t("testimonials.r2.t3"), name: "Michael Lee", role: t("testimonials.role.formerManualTrader"), image: "/frenpfp.png" },
    { text: t("testimonials.r2.t4"), name: "Sophia Martinez", role: t("testimonials.role.professionalBettor"), image: "/frenpfp.png" },
  ];
  return (
    <section className="relative w-full overflow-hidden bg-gray-50 dark:bg-[#0f0f12] py-16 sm:py-24">
      {/* Header */}
      <div className="relative z-10 px-6 mx-auto mb-12 text-center max-w-7xl sm:mb-16 lg:px-12">
        <p className="text-sm font-monument font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
          {t("testimonials.eyebrow")}
        </p>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-monument font-bold text-gray-900 dark:text-white mb-4">
          {t("testimonials.headline.before")}{" "}
          <span className="text-accent-primary">{t("testimonials.headline.accent")}</span>
        </h2>
        <p className="mx-auto mt-3 max-w-2xl text-lg font-satoshi text-gray-500 dark:text-gray-400">
          {t("testimonials.subtitle")}
        </p>
        <div className="mt-6 inline-flex items-center gap-2 rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] bg-white dark:bg-[#18181D] px-5 py-2.5">
          {Array.from({ length: 5 }).map((_, i) => (
            <FaStar key={i} className="text-amber-400 text-lg" />
          ))}
          <span className="ml-1 text-sm font-inter-tight font-bold text-gray-900 dark:text-white">4.9/5</span>
          <span className="text-sm font-satoshi text-gray-500 dark:text-gray-400">&middot; {t("testimonials.tradersCount")}</span>
        </div>
      </div>

      {/* Marquee full-bleed */}
      <div className="relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen overflow-x-clip">
        <MarqueeRow baseItems={ROW_A} direction="left" durationSec={42} />
        <MarqueeRow baseItems={ROW_B} direction="right" durationSec={50} />
      </div>

      <style jsx global>{`
        @keyframes marquee-left {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes marquee-right {
          0% { transform: translateX(-50%); }
          100% { transform: translateX(0); }
        }
        .marquee-track {
          backface-visibility: hidden;
          will-change: transform;
          contain: paint;
        }
        @media (prefers-reduced-motion: reduce) {
          .marquee-track {
            animation: none !important;
            transform: translateX(0) !important;
          }
        }
      `}</style>
    </section>
  );
}

function MarqueeRow({
  baseItems,
  direction,
  durationSec,
}: {
  baseItems: Testimonial[];
  direction: "left" | "right";
  durationSec: number;
}) {
  const [paused, setPaused] = useState(false);

  return (
    <div className="relative w-full px-6 py-4 overflow-x-hidden sm:px-12">
      <div
        className="flex gap-6 marquee-track w-max"
        style={{
          animation: `${direction === "left" ? "marquee-left" : "marquee-right"} ${durationSec}s linear infinite`,
          animationPlayState: paused ? "paused" : "running",
        }}
      >
        <Row items={baseItems} setPaused={setPaused} />
        <Row items={baseItems} setPaused={setPaused} />
      </div>
    </div>
  );
}

function Row({
  items,
  setPaused,
}: {
  items: Testimonial[];
  setPaused: (v: boolean) => void;
}) {
  return (
    <div className="flex gap-6 shrink-0">
      {items.map((t, i) => (
        <Card
          key={`${t.name}-${i}`}
          t={t}
          index={i}
          setPaused={setPaused}
        />
      ))}
    </div>
  );
}

function Card({
  t,
  index,
  setPaused,
}: {
  t: Testimonial;
  index: number;
  setPaused: (v: boolean) => void;
}) {
  const widths = [
    "sm:w-[680px] w-[84vw]",
    "sm:w-[520px] w-[84vw]",
    "sm:w-[740px] w-[84vw]",
    "sm:w-[460px] w-[84vw]",
    "sm:w-[600px] w-[84vw]",
  ];
  const w = widths[index % widths.length];

  return (
    <div className="relative px-2 py-2 shrink-0">
      <article
        tabIndex={0}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
        className={`${w} relative h-[190px] rounded-[5px] bg-white dark:bg-[#18181D]
                    px-7 py-8 border border-gray-200 dark:border-[#1A1A1A]
                    hover:bg-gray-50 dark:hover:bg-[#1e1e24] transition-colors
                    outline-none focus-visible:ring-2 focus-visible:ring-gray-300 dark:focus-visible:ring-gray-600
                    flex flex-col overflow-hidden`}
      >
        {/* Quote icon */}
        <svg
          aria-hidden
          className="pointer-events-none absolute top-3 left-3 h-8 w-8 opacity-[0.08] text-gray-400 dark:text-gray-500"
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d="M7 7h5v5H9v3H7V7zm10 0h-5v5h3v3h2V7z" />
        </svg>

        <p className="relative z-10 text-[15px] font-satoshi leading-snug text-gray-600 dark:text-gray-300 antialiased line-clamp-3">
          {t.text}
        </p>

        <div className="relative z-10 flex items-center gap-3 pt-4 mt-auto">
          <CdnImage
            src={t.image}
            alt={t.name}
            width={40}
            height={40}
            className="object-cover w-10 h-10 rounded-[5px]"
          />
          <div>
            <p className="text-sm font-monument font-semibold text-gray-900 dark:text-white">{t.name}</p>
            <p className="text-xs font-satoshi text-gray-500 dark:text-gray-400">{t.role}</p>
          </div>
          <span className="ml-auto rounded-[5px] bg-gray-100 dark:bg-[#0f0f12] border border-gray-200 dark:border-[#1A1A1A] px-3 py-1 text-[11px] font-inter-tight font-bold text-gray-900 dark:text-white">
            Verified
          </span>
        </div>
      </article>
    </div>
  );
}
